/**
 * Sign in, plus the password-reset request and the set-new-password screens.
 *
 * Base44 hosted authentication, so none of this existed in the export — the
 * app redirected out to their login page and came back with a token in the URL.
 */

import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MIN_PASSWORD_LENGTH } from '@stockwise/shared/passwords';

import { BrandMark } from '@/components/BrandMark.jsx';
import { useAuth } from '@/lib/auth-context.jsx';
import { acceptInvite, describeInvite, resetPassword } from '@/lib/auth-client.js';

/** Kept in step with the server, which refuses anything shorter. */

/**
 * The frame around every unauthenticated screen.
 *
 * Two panels on a wide display: the brand on the left, the form on the right.
 * On a phone the brand panel collapses to a compact header so the form stays
 * above the fold with a keyboard open.
 */
function Shell({ title, description, children }) {
  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Brand panel. The top padding includes the status bar, which this
          panel sits under when the app is installed to an iPhone home screen. */}
      <div className="brand-gradient relative flex shrink-0 items-center justify-center px-6 pb-8 pt-[calc(2rem+env(safe-area-inset-top))] lg:w-[44%] lg:py-12">
        <div className="relative w-full max-w-sm text-center lg:text-left">
          <div className="flex items-center justify-center gap-3 lg:justify-start">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white ring-1 ring-white/20">
              <BrandMark className="h-7 w-7" />
            </div>
            <span className="text-2xl font-semibold tracking-tight text-white">StockWise</span>
          </div>

          <p className="mt-4 hidden text-lg font-medium text-white/90 lg:block">
            Know what you have, and what to order next.
          </p>

          <ul className="mt-8 hidden space-y-3 text-sm text-white/70 lg:block">
            {[
              'Stock levels across every product and variant',
              'Purchase orders drafted automatically when stock runs low',
              'Barcode-driven receiving and allocation',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-sm">
          <Card className="card-elevated shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl">{title}</CardTitle>
              {description ? <CardDescription>{description}</CardDescription> : null}
            </CardHeader>
            <CardContent>{children}</CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Gold Star Printing &middot; StockWise
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SignIn() {
  const { signIn, sendPasswordReset } = useAuth();
  const [mode, setMode] = useState('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  async function handleSignIn(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      // The auth listener takes it from here.
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await sendPasswordReset(email.trim());
      setNotice(
        `If an account exists for ${email.trim()}, a reset link is on its way. The link is valid for one hour.`,
      );
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'reset') {
    return (
      <Shell title="Reset your password" description="We will email you a link to set a new one.">
        <form onSubmit={handleReset} className="space-y-4">
          {notice ? (
            <Alert>
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              id="reset-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send reset link
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setMode('sign-in');
              setError(null);
              setNotice(null);
            }}
          >
            Back to sign in
          </Button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title="Sign in" description="Use the account an administrator set up for you.">
      <form onSubmit={handleSignIn} className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Sign in
        </Button>

        <Button
          type="button"
          variant="link"
          className="w-full"
          onClick={() => {
            setMode('reset');
            setError(null);
          }}
        >
          Forgot your password?
        </Button>
      </form>
    </Shell>
  );
}

/**
 * "Choose a password", reached from an invitation or a reset link.
 *
 * Both links carry a one-time token in the query string. Nothing here needs a
 * session — which is why this screen is registered once, above the auth gate,
 * rather than twice as it was when a Supabase link arrived with a session in
 * the URL fragment and the app had to be ready for it in either branch.
 *
 * The two modes differ only in wording and in which endpoint spends the token.
 */
export function SetPassword() {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = params.get('token');

  const isInvite = location.pathname.startsWith('/accept-invite');

  const [account, setAccount] = useState(null);
  const [checking, setChecking] = useState(isInvite && Boolean(token));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dead, setDead] = useState(!token);
  const [done, setDone] = useState(false);

  // Naming the account is a courtesy; failing fast on a dead link is the point.
  // Otherwise someone types a password twice before being told the link expired.
  useEffect(() => {
    if (!isInvite || !token) return undefined;

    let active = true;
    describeInvite(token).then((found) => {
      if (!active) return;
      if (found) setAccount(found);
      else setDead(true);
      setChecking(false);
    });

    return () => {
      active = false;
    };
  }, [isInvite, token]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      if (isInvite) await acceptInvite(token, password);
      else await resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      // 410 means the link itself is finished, which is not something retyping
      // the password can fix — so swap the form for an explanation.
      if (caught.status === 410) setDead(true);
      else setError(caught.message);
    } finally {
      setBusy(false);
    }
  }

  if (dead) {
    return (
      <Shell
        title={isInvite ? 'This invitation is no longer valid' : 'This link is no longer valid'}
        description={
          isInvite
            ? 'Invitations expire after seven days and can only be used once. Ask an administrator to send a new one.'
            : 'Reset links expire after an hour and can only be used once.'
        }
      >
        <Button className="w-full" onClick={() => window.location.assign('/')}>
          Back to sign in
        </Button>
      </Shell>
    );
  }

  if (checking) {
    return (
      <Shell title="Checking your invitation">
        <div className="flex justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Password set" description="You are signed in and ready to go.">
        <Button className="w-full" onClick={() => window.location.assign('/')}>
          Continue to StockWise
        </Button>
      </Shell>
    );
  }

  return (
    <Shell
      title={isInvite ? 'Welcome to StockWise' : 'Choose a new password'}
      description={
        account?.email
          ? `Setting the password for ${account.email}.`
          : isInvite
            ? 'Choose a password to finish setting up your account.'
            : undefined
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            At least {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save password
        </Button>
      </form>
    </Shell>
  );
}
